// Clavier à l'écran et verrou de défilement du chatbot.
//
// --app-kb sur <html> : hauteur que couvre le clavier en bas du viewport de
// mise en page ; le panneau (position: fixed) s'arrête juste au-dessus. Dans
// l'app, seul l'événement de @capacitor/keyboard renseigne (hauteur sur
// event.keyboardHeight) : visualViewport y reste inerte, et il est ignoré pour
// de bon dès le premier événement natif (sous zoom, il inventerait un
// clavier). Dans un navigateur mobile, visualViewport fait foi. Au focus d'un
// champ, levée optimiste (dernière hauteur mesurée, sinon 40 % de l'écran)
// sans attendre l'événement natif, qui arrive tard ; abandonnée après 2,5 s
// sans clavier (clavier matériel).
//
// Seul écrivain de --app-kb sur <html> (lu aussi par .plan-chat) ; suivi dès
// le montage du widget, pas seulement panneau ouvert. Tout est retiré au
// démontage.
import { useCallback, useEffect, useRef } from 'react'
import { isFullscreenPanel } from '@/components/chatbot/format'

// Interrupteur : ne croire que l'événement clavier natif de Capacitor.
const NATIVE_KEYBOARD_ONLY = false
const MIN_BROWSER_KEYBOARD = 80
const MAX_KEYBOARD_FRACTION = 0.75
const PREDICTED_KEYBOARD_FRACTION = 0.4
const OPTIMISTIC_LIFT_TIMEOUT = 2500

type KeyboardEventLike = Event & { keyboardHeight?: number }

/** Verrou de défilement de <html> (plein écran seulement). */
export function useChatbotScrollLock() {
  const state = useRef<{ locked: boolean; savedScrollY: number | null }>({ locked: false, savedScrollY: null })

  const lock = useCallback(() => {
    const s = state.current
    if (s.locked || !isFullscreenPanel()) return
    s.locked = true
    s.savedScrollY = window.scrollY || window.pageYOffset || 0
    const rootStyle = document.documentElement.style
    rootStyle.setProperty('overflow', 'hidden', 'important')
    rootStyle.setProperty('overscroll-behavior', 'none', 'important')
    document.body.classList.add('chatbot-open')
  }, [])

  const unlock = useCallback((restoreScroll: boolean) => {
    const s = state.current
    document.body.classList.remove('chatbot-open')
    if (!s.locked) return
    s.locked = false
    const rootStyle = document.documentElement.style
    rootStyle.removeProperty('overflow')
    rootStyle.removeProperty('overscroll-behavior')
    // Double rAF : iOS borne le défilement tant que la mise en page n'est pas revenue.
    const y = s.savedScrollY
    s.savedScrollY = null
    if (restoreScroll && typeof y === 'number') {
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, y)))
    }
  }, [])

  // Démontage : jamais de restauration (la page a changé), mais jamais de
  // verrou laissé sur <html>.
  useEffect(() => () => unlock(false), [unlock])

  return { lock, unlock }
}

/**
 * Suivi de la hauteur du clavier. `onInsetChange` est appelé (dans un rAF)
 * quand l'inset change ; le widget y fait défiler les messages s'il est ouvert.
 * Renvoie `update` (recalcul immédiat, appelé à l'ouverture).
 */
export function useChatbotKeyboard(onInsetChange: () => void) {
  const onInsetChangeRef = useRef(onInsetChange)
  onInsetChangeRef.current = onInsetChange
  const updateRef = useRef<() => void>(() => {})

  useEffect(() => {
    let nativeKeyboardSeen = false
    let nativeKeyboardHeight = 0
    let lastKeyboardInset: number | null = null
    let optimisticKeyboardInset = 0
    let optimisticKeyboardTimer: number | null = null
    let lastRealKeyboardHeight = 0

    const clampKeyboardInset = (px: number) => {
      const v = Math.round(Number(px))
      if (!Number.isFinite(v) || v <= 0) return 0
      const max = Math.round(document.documentElement.clientHeight * MAX_KEYBOARD_FRACTION)
      return v > max ? 0 : v
    }

    const update = () => {
      let kb = 0
      if (nativeKeyboardSeen) {
        kb = clampKeyboardInset(nativeKeyboardHeight)
      } else if (!NATIVE_KEYBOARD_ONLY) {
        const vv = window.visualViewport
        // Zoom : l'arithmétique inventerait un clavier de plusieurs centaines de px.
        if (vv && Math.abs((vv.scale || 1) - 1) <= 0.01) {
          const raw = Math.round(window.innerHeight - vv.height - vv.offsetTop)
          kb = raw >= MIN_BROWSER_KEYBOARD ? clampKeyboardInset(raw) : 0
        }
      }
      if (kb === 0 && optimisticKeyboardInset > 0) kb = optimisticKeyboardInset

      if (kb === lastKeyboardInset) return
      lastKeyboardInset = kb
      document.documentElement.style.setProperty('--app-kb', `${kb}px`)
      requestAnimationFrame(() => onInsetChangeRef.current())
    }
    updateRef.current = update

    // Dernière hauteur réelle : sessionStorage, pour la page suivante.
    const rememberKeyboardHeight = (px: number) => {
      lastRealKeyboardHeight = px
      try { sessionStorage.setItem('chatbotKbHeight', String(px)) } catch { /* ignore */ }
    }

    const predictKeyboardInset = () => {
      let px = Number(lastRealKeyboardHeight)
      if (!Number.isFinite(px) || px <= 0) {
        try { px = Number(sessionStorage.getItem('chatbotKbHeight')) } catch { px = 0 }
      }
      if (!Number.isFinite(px) || px <= 0) {
        px = Math.round(document.documentElement.clientHeight * PREDICTED_KEYBOARD_FRACTION)
      }
      return clampKeyboardInset(px)
    }

    const disarmOptimisticLift = (recompute: boolean) => {
      if (optimisticKeyboardTimer) {
        window.clearTimeout(optimisticKeyboardTimer)
        optimisticKeyboardTimer = null
      }
      if (!optimisticKeyboardInset) return
      optimisticKeyboardInset = 0
      if (recompute) update()
    }

    // Capacitor : la hauteur est sur l'événement lui-même (event.keyboardHeight).
    const onNativeKeyboardShow = (event: Event) => {
      nativeKeyboardSeen = true
      const h = Number((event as KeyboardEventLike).keyboardHeight)
      nativeKeyboardHeight = Number.isFinite(h) && h > 0 ? Math.round(h) : 0
      if (nativeKeyboardHeight > 0) rememberKeyboardHeight(nativeKeyboardHeight)
      disarmOptimisticLift(false)
      update()
    }
    const onNativeKeyboardHide = () => {
      nativeKeyboardSeen = true
      nativeKeyboardHeight = 0
      disarmOptimisticLift(false)
      update()
    }
    const onViewportChange = () => update()

    const isTextEntry = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null
      return !!node && (node.tagName === 'INPUT' || node.tagName === 'TEXTAREA' || node.isContentEditable)
    }

    // Levée optimiste : dès le focus d'un champ, sans attendre l'événement natif.
    const onComposerFocus = (event: FocusEvent) => {
      if (!isTextEntry(event.target)) return
      if (!isFullscreenPanel()) return
      if (nativeKeyboardHeight > 0) return
      optimisticKeyboardInset = predictKeyboardInset()
      if (optimisticKeyboardTimer) window.clearTimeout(optimisticKeyboardTimer)
      optimisticKeyboardTimer = window.setTimeout(() => {
        optimisticKeyboardTimer = null
        disarmOptimisticLift(true)
      }, OPTIMISTIC_LIFT_TIMEOUT)
      update()
    }
    const onComposerBlur = (event: FocusEvent) => {
      if (!isTextEntry(event.target)) return
      disarmOptimisticLift(true)
    }

    window.addEventListener('keyboardWillShow', onNativeKeyboardShow)
    window.addEventListener('keyboardDidShow', onNativeKeyboardShow)
    window.addEventListener('keyboardWillHide', onNativeKeyboardHide)
    window.addEventListener('keyboardDidHide', onNativeKeyboardHide)
    document.addEventListener('focus', onComposerFocus, true)
    document.addEventListener('blur', onComposerBlur, true)
    const vv = window.visualViewport
    if (vv) {
      vv.addEventListener('resize', onViewportChange)
      vv.addEventListener('scroll', onViewportChange)
    }
    window.addEventListener('orientationchange', onViewportChange)
    window.addEventListener('resize', onViewportChange)

    update()

    return () => {
      window.removeEventListener('keyboardWillShow', onNativeKeyboardShow)
      window.removeEventListener('keyboardDidShow', onNativeKeyboardShow)
      window.removeEventListener('keyboardWillHide', onNativeKeyboardHide)
      window.removeEventListener('keyboardDidHide', onNativeKeyboardHide)
      if (vv) {
        vv.removeEventListener('resize', onViewportChange)
        vv.removeEventListener('scroll', onViewportChange)
      }
      window.removeEventListener('orientationchange', onViewportChange)
      window.removeEventListener('resize', onViewportChange)
      document.removeEventListener('focus', onComposerFocus, true)
      document.removeEventListener('blur', onComposerBlur, true)
      if (optimisticKeyboardTimer) window.clearTimeout(optimisticKeyboardTimer)
      updateRef.current = () => {}
      document.documentElement.style.removeProperty('--app-kb')
    }
  }, [])

  return useCallback(() => updateRef.current(), [])
}
